import React, { useEffect, useMemo } from 'react';
import axios from 'axios';
import {BrowserRouter, Routes, Route} from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ModoOscuroProvider, useModoOscuro } from './contexts/ModoOscuro';
import { API_BASE_URL, FACTURACION_GET } from './services/apiConfig';
import '@fontsource/roboto/400.css';
import './App.css';
import './bootstrap-grid.min.css';
import Footer from './components/Footer';
import Register from './components/Register';
import Login from './components/Login';
import Logout from './components/Logout';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './components/Dashboard';
import User from './components/User';
import UserList from './components/UserList';
import UsuarioAccionesList from './components/UsuarioAccionesList';
import AuditoriaList from './components/AuditoriaList';
import ErroresList from './components/ErroresList';
import FacturacionConfig from './components/FacturacionConfig';
import GlobalErrorToast from './components/GlobalErrorToast';
import GridoCompetencia from './components/GridoCompetencia';
import RegistrosProductosList from './components/RegistrosProductosList';
import RegistroProducto from './components/RegistroProducto';
import ProductosList from './components/ProductosList';
import Producto from './components/Producto';
import GruposList from './components/GruposList';
import Grupo from './components/Grupo';
import SucursalesList from './components/SucursalesList';
import Sucursal from './components/Sucursal';
import TurnosList from './components/TurnosList';
import Turno from './components/Turno';
import Analisis from './components/Analisis';
import PreciosImportar from './components/PreciosImportar';
import MapeoPrecios from './components/MapeoPrecios';
import ChecklistTareas from './components/ChecklistTareas';
import ReporteAnulaciones from './components/ReporteAnulaciones';

const DEFAULT_COLOR = '#071c44';
const DEFAULT_ICON_COLOR = '#a9b9a6';
const DEFAULT_NOMBRE = 'Sistema de Gestión';

function applyTheme({ color, iconColor, nombre }) {
  if (color) document.documentElement.style.setProperty('--color-sistema', color);
  if (iconColor) document.documentElement.style.setProperty('--color-icono', iconColor);
  if (nombre) document.title = nombre;
}

function AppContenido() {
  const { oscuro } = useModoOscuro();
  const theme = useMemo(() => createTheme({ palette: { mode: oscuro ? 'dark' : 'light' } }), [oscuro]);

  useEffect(() => {
    // 1) Si el deployment cambió respecto a la última visita, limpiar cache
    //    de tema. localStorage es compartido entre instancias que conviven
    //    en el mismo origin (agilia.com.ar/...). Sin esto, al saltar entre locales
    //    se ven los colores/nombre de la última que visitaste.
    const currentDeploy = API_BASE_URL || '';
    const cachedDeploy = localStorage.getItem('__deploy_id');
    if (cachedDeploy !== currentDeploy) {
      localStorage.removeItem('color_sistema');
      localStorage.removeItem('color_icono');
      localStorage.removeItem('nombre_sistema');
      localStorage.setItem('__deploy_id', currentDeploy);
    }

    // 2) Pintar con lo que haya en cache (o defaults) para evitar flash
    applyTheme({
      color: localStorage.getItem('color_sistema') || DEFAULT_COLOR,
      iconColor: localStorage.getItem('color_icono') || DEFAULT_ICON_COLOR,
      nombre: localStorage.getItem('nombre_sistema') || DEFAULT_NOMBRE,
    });

    // 3) Si hay sesión, refrescar desde el backend en cada carga. Esto evita
    //    que cambios hechos en el servidor (color, nombre del sistema) queden
    //    cacheados hasta que el usuario abra FacturacionConfig.
    const token = sessionStorage.getItem('authToken');
    if (!token) return;

    axios.get(API_BASE_URL + FACTURACION_GET, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      const cfg = res.data?.data ?? res.data ?? {};
      const fresh = {
        color: cfg.color_primario || DEFAULT_COLOR,
        iconColor: cfg.color_icono || DEFAULT_ICON_COLOR,
        nombre: cfg.nombre_sistema || DEFAULT_NOMBRE,
      };
      localStorage.setItem('color_sistema', fresh.color);
      localStorage.setItem('color_icono', fresh.iconColor);
      localStorage.setItem('nombre_sistema', fresh.nombre);
      applyTheme(fresh);
    }).catch(() => {
      // Silencioso: si falla, mantenemos lo que ya pintamos en el paso 2.
    });
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div>

        <BrowserRouter>
          <Routes>
            <Route index element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/logout" element={<Logout />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/userList" element={<ProtectedRoute perfilRequerido={[1, 2]}><UserList /></ProtectedRoute>} />
            <Route path="/user" element={<ProtectedRoute><User /></ProtectedRoute>} />
            <Route path="/UsuarioAccionesList" element={<ProtectedRoute perfilRequerido={1}><UsuarioAccionesList /></ProtectedRoute>} />
            <Route path="/AuditoriaList" element={<ProtectedRoute perfilRequerido={1}><AuditoriaList /></ProtectedRoute>} />
            <Route path="/ErroresList" element={<ProtectedRoute perfilRequerido={1}><ErroresList /></ProtectedRoute>} />
            <Route path="/facturacionconfig" element={<ProtectedRoute perfilRequerido={1}><FacturacionConfig /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute perfilRequerido={1}><FacturacionConfig /></ProtectedRoute>} />
            <Route path="/grido-competencia" element={<ProtectedRoute><GridoCompetencia /></ProtectedRoute>} />
            <Route path="/registros-productos" element={<ProtectedRoute><RegistrosProductosList /></ProtectedRoute>} />
            <Route path="/reporte-anulaciones" element={<ProtectedRoute perfilRequerido={[1, 2]}><ReporteAnulaciones /></ProtectedRoute>} />
            <Route path="/registro-producto" element={<ProtectedRoute><RegistroProducto /></ProtectedRoute>} />
            <Route path="/productos" element={<ProtectedRoute perfilRequerido={[1, 2]}><ProductosList /></ProtectedRoute>} />
            <Route path="/producto" element={<ProtectedRoute perfilRequerido={[1, 2]}><Producto /></ProtectedRoute>} />
            <Route path="/grupos" element={<ProtectedRoute perfilRequerido={1}><GruposList /></ProtectedRoute>} />
            <Route path="/grupo" element={<ProtectedRoute perfilRequerido={1}><Grupo /></ProtectedRoute>} />
            <Route path="/sucursales" element={<ProtectedRoute perfilRequerido={[1, 2]}><SucursalesList /></ProtectedRoute>} />
            <Route path="/sucursal" element={<ProtectedRoute perfilRequerido={[1, 2]}><Sucursal /></ProtectedRoute>} />
            <Route path="/turnos" element={<ProtectedRoute><TurnosList /></ProtectedRoute>} />
            <Route path="/turno" element={<ProtectedRoute><Turno /></ProtectedRoute>} />
            <Route path="/analisis" element={<ProtectedRoute perfilRequerido={[1, 2]}><Analisis /></ProtectedRoute>} />
            <Route path="/precios" element={<ProtectedRoute perfilRequerido={[1, 2]}><PreciosImportar /></ProtectedRoute>} />
            <Route path="/mapeo-precios" element={<ProtectedRoute perfilRequerido={[1, 2]}><MapeoPrecios /></ProtectedRoute>} />
            <Route path="/checklist-tareas" element={<ProtectedRoute perfilRequerido={[1, 2]}><ChecklistTareas /></ProtectedRoute>} />

          </Routes>
          <Footer />
          <GlobalErrorToast />
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ModoOscuroProvider>
      <AppContenido />
    </ModoOscuroProvider>
  );
}

export default App;